import React, {useEffect} from 'react';
import DOMPurify from 'dompurify';
import styles from './NewRequestForInformation.module.css';
import {useLocation, useParams, useSearchParams} from "react-router-dom";
import {useAuth} from "react-oidc-context";
import {
    fetchNewRequestInfoById,
    postNewRequest,
    putDraftRequest
} from "../../services/RequestServices/RequestInfoService";
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IconButton,
    IDropdownOption,
    IModalProps,
    IStackStyles,
    IStackTokens, ITextFieldProps,
    Label,
    MessageBar,
    MessageBarType,
    Pivot,
    PivotItem,
    PrimaryButton,
    Stack,
    StackItem,
    TextField
} from "@fluentui/react";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";
import strings from "../../loc/strings";
import InitiatorPoiFieldsComponent from "../RequestForInformation/RequestForInfoComponents/InitiatorPoiFieldsComponent";
import {Dropdown} from "@fluentui/react/lib/Dropdown";
import {NewRequestDto, newRequestNcaDto, RequestCaDto} from "../../model/NewRequestDto";
import {RecipientAndRequestDto} from "../../model/RecipientAndRequestDto";
import {
    INewRequestForInfoItem,
    INewRequestForInfoItemError,
    initNewRequestForInfoItem
} from "../../model/NewRequestForInfoItem";
import {useBoolean} from "@fluentui/react-hooks";
import NcasDetailsListWithPagination from "./NcasDetailsListWIthPagination/NcasDetailsListWithPagination";
import {HTTP_STATUS_UNAUTHORIZED} from "../../constants/HttpStatusCodes";
import ReactDOM from "react-dom";
import {displayMessageBar} from "../LayoutWithAuth/LayoutWithAuth";
import {Status} from "../RequestForInformation/RequestForInfoUtils";
import {NewRequestEmailError} from "../../model/NewRequestEmailError";
import {fetchCoreFieldGuidance} from "../../services/FieldGuidanceService";

interface NewRequestForInformationProps {}

interface FormData {
    caInternalAssessmentNumber: string,
    dateOfAssessment: string,
    euSectoralBasis: number | null,
    reasonForRequest: string,
    requestedInfo: string,
    formState: FormDataState
}

interface FormDataState {
    euSectoralBasis: boolean | null,
    reasonForRequest: boolean| null,
    requestedInfo: boolean| null,
}

const themedLargeStackTokens: IStackTokens = {
    childrenGap: 's1',
    padding: '0 0 30px 0'
};

const stackStylesLabels: Partial<IStackStyles> = {
    root: {
        paddingTop: '30px',
        paddingBottom: '20px'
    },
};

const buttonStackTokens: IStackTokens = {
    childrenGap: '10'
};

const modalProps: IModalProps = {
    isBlocking: true,
};

const editRecipientsDialogStyle = {
    main: {
        selectors: {
            ['@media (min-width: 480px)']: {
                width: '100%',
                maxWidth: '1100px',
            },
            ['.ms-List-page .ms-List-cell:empty']: {
                display: 'none',
            },
        },
    }
}
function NewRequestForInformation(props: NewRequestForInformationProps) {
    const { poiId } = useParams();

    const auth = useAuth();
    const [state, setState] = React.useState<INewRequestForInfoItem>(initNewRequestForInfoItem());
    const [formDatas, setFormDatas] = React.useState<{[key: string] : FormData}>({});
    const [selectedTab, setSelectedTab] = React.useState("0");
    const [recipientsToShow, setRecipientsToShow] = React.useState<RecipientAndRequestDto[]>([]);
    const [isCopyHidden, setIsCopyHidden] = React.useState(false);
    const [hideCopyDialog, { toggle: toggleHideCopyDialog }] = useBoolean(true);
    const [hideSubmitDialog, { toggle: toggleHideSubmitDialog }] = useBoolean(true);
    const [hideDraftDialog, { toggle: toggleHideDraftDialog }] = useBoolean(true);
    const [hideRecipientsDialog, { toggle: toggleHideRecipientsDialog }] = useBoolean(true);
    const [isUnauthorized, setIsUnauthorized] = React.useState(false);
    const [formSubmitted, setFormSubmitted] = React.useState(false);
    const [fieldGuidance, setFieldGuidance] = React.useState({ reasonForRequest: '', infoRequested: '' });

    const { search } = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryParams = new URLSearchParams(search);
    const assessmentId = queryParams.get('assessmentId');
    const [assessmentIdArray] = React.useState<string[]>(assessmentId ? assessmentId.split(',') : []);
    const reqId = queryParams.get('reqId') ?? undefined;
    const [draftId, setDraftId] = React.useState<string | undefined>(reqId);

    let newSelectedItems: RecipientAndRequestDto[];
    let initFormDatas: {[key: string] : FormData} = {};
    const maxCharLimit = 4000;

    function handleSelectedItemsChange(items: RecipientAndRequestDto[]) {
        newSelectedItems = items;
    }
    function createEmptyFormData(): FormData {
        return {
            caInternalAssessmentNumber: '',
            dateOfAssessment: '',
            euSectoralBasis: null,
            reasonForRequest: '',
            requestedInfo: '',
            formState: {
                euSectoralBasis: false,
                reasonForRequest: false,
                requestedInfo: false,
            }
        };
    }

    useEffect(() => {
        (async function () {
            const response = await fetchNewRequestInfoById(poiId ?? "", assessmentIdArray, auth.user?.id_token, draftId);
            // check response is  INewRequestForInfoItemError
            if ((response as INewRequestForInfoItemError).statusCode === HTTP_STATUS_UNAUTHORIZED) {
                displayMessageBar(MessageBarType.error, strings.newRequestForInfoUnauthorised, false);
                // return;
                setIsUnauthorized(true);
                setState(initNewRequestForInfoItem());
            }
            if (response as INewRequestForInfoItem) {
                let responseOk = response as INewRequestForInfoItem;
                if(responseOk.statusTypeId === Status.Submitted) {
                    setFormSubmitted(true);
                }
                setState({
                    id: responseOk.id,
                    initiatorDetails: responseOk.initiatorDetails,
                    personOfInterest: responseOk.personOfInterest,
                    statusTypeId: responseOk.statusTypeId,
                    recipientAndRequestDetails: responseOk.recipientAndRequestDetails,
                    euSectoralLegislatureBasis: responseOk.euSectoralLegislatureBasis.sort((a, b) => a.name.localeCompare(b.name)),
                });
            }
        })();
    }, []);

    useEffect(() => {
        fetchCoreFieldGuidance(auth.user?.id_token)
            .then(setFieldGuidance)
            .catch((error) => { console.error('Error fetching field guidance:', error); });
    }, []);

    useEffect(() => {
        if (state?.recipientAndRequestDetails?.length) {
            if(draftId || assessmentIdArray.length > 0){
                const selectedRecipients = draftId ? state.recipientAndRequestDetails.filter(recipient => recipient.caResponseId) :
                    state.recipientAndRequestDetails.filter(recipient => recipient.isSelected);
                const initialFormData: { [key: string]: FormData } = {};
                selectedRecipients.forEach((item) => {
                    initialFormData[item.id] = createEmptyFormData();
                    initialFormData[item.id].caInternalAssessmentNumber = item.caInternalAssessmentNumber;
                    initialFormData[item.id].dateOfAssessment = item.dateOfAssessment;
                    initialFormData[item.id].euSectoralBasis = item.caResponseEuSectoralLegislatureBasisId ?? null;
                    initialFormData[item.id].reasonForRequest = item.reasonForRequest;
                    initialFormData[item.id].requestedInfo = item.requestInformation;
                });
                // setFormDatas(initialFormData);
                initFormDatas = {...initialFormData};
                setRecipients(selectedRecipients, initialFormData);
            } else {
                setRecipientsToShow(state.recipientAndRequestDetails);
                const initialFormData: { [key: string]: FormData } = {};
                state.recipientAndRequestDetails.forEach((item) => {
                    initialFormData[item.id] = createEmptyFormData();
                    initialFormData[item.id].caInternalAssessmentNumber = item.caInternalAssessmentNumber;
                    initialFormData[item.id].dateOfAssessment = item.dateOfAssessment;
                });
                setFormDatas(initialFormData);
                initFormDatas = {...initialFormData};
                // setSelectedTab(Object.keys(initialFormData)[0]);
            }
        }
    }, [state.recipientAndRequestDetails]);

    useEffect(() => {
        const selTab = recipientsToShow[0]?.id.toString();
        if(selTab && formDatas[selTab]) {
            setSelectedTab(selTab);
        }
        setIsCopyHidden(recipientsToShow.length < 2)
    }, [recipientsToShow]);

    function _handleInputChange(pivotIndex: number, fieldName: keyof FormData, value: string | undefined): void {
        if (fieldName !== 'euSectoralBasis' && fieldName !== 'formState' && (!value || value.length <= 4000)) {
            const updatedFormDatas = {...formDatas};
            updatedFormDatas[pivotIndex][fieldName] = value ?? '';
            updatedFormDatas[pivotIndex].formState = {
                ...updatedFormDatas[pivotIndex].formState,
                [fieldName]: true
            };
            setFormDatas(updatedFormDatas);
        }
    }
    const _handleDropDownChange = (recipientAndRequestDto: RecipientAndRequestDto, fieldName: keyof FormData, item: IDropdownOption | undefined) => {
        const updatedFormDatas = {...formDatas};
        updatedFormDatas[recipientAndRequestDto.id].euSectoralBasis = item?.key as number ?? null;
        updatedFormDatas[recipientAndRequestDto.id].formState = {
            ...updatedFormDatas[recipientAndRequestDto.id].formState,
            [fieldName]: true
        }
        setFormDatas(updatedFormDatas);
    };

    const handleTabClick = (item: PivotItem | undefined) => {
        if (item?.props.itemKey) {
            setSelectedTab(item.props.itemKey);
        }
    };

    const copyDialogContentProps = {
        type: DialogType.normal,
        title: 'Copy data',
        closeButtonAriaLabel: 'Cancel',
        subText: 'All provided information will be copied to all recipients (overriding any existing data). Are you sure you want to continue?',
        showCloseButton: true
    };

    const submitDialogContentProps = {
        type: DialogType.normal,
        title: 'Submit',
        closeButtonAriaLabel: 'Cancel',
        subText: 'A new request for information is about to be submitted to the selected recipients. Are you sure you want to continue?',
        showCloseButton: true
    };

    const draftDialogContentProps = {
        type: DialogType.normal,
        title: 'Draft',
        closeButtonAriaLabel: 'Cancel',
        subText: 'A new request for information is about to be saved as draft. Are you sure you want to continue?',
        showCloseButton: true
    };

    const recipientsDialogContentProps = {
        type: DialogType.normal,
        title: 'Select CA recipients',
        closeButtonAriaLabel: 'Cancel',
        subText: '',
        showCloseButton: true
    };

    function _copyData() {
        if (selectedTab && formDatas[selectedTab]) {
            const selectedPivotData = formDatas[selectedTab];
            const updatedFormDatas = {...formDatas};

            Object.keys(updatedFormDatas).forEach((key) => {
                if (key !== selectedTab) {
                    updatedFormDatas[key].reasonForRequest = selectedPivotData.reasonForRequest;
                    updatedFormDatas[key].requestedInfo = selectedPivotData.requestedInfo;
                    updatedFormDatas[key].euSectoralBasis = selectedPivotData.euSectoralBasis;
                }
            });
            setFormDatas(updatedFormDatas);
        }
        toggleHideCopyDialog()
    }

    function convertFormDataToDto(): RequestCaDto[] {
        const requests: RequestCaDto[] = [];

        Object.keys(formDatas).forEach((ca) => {
            const formData = formDatas[ca];
            const nca = state.recipientAndRequestDetails.find(nca => nca.id === Number(ca));
            if (nca) {
                const ncaDto: newRequestNcaDto = {
                    Name: nca.caName,
                    Code: nca.caCode
                }
                const requestDto: RequestCaDto = {
                    ncaDto: ncaDto,
                    euSecLegBasis: formData.euSectoralBasis,
                    reasonForRequest: formData.reasonForRequest,
                    infoRequested: formData.requestedInfo,
                    assessmentId: Number(ca),
                };
                requests.push(requestDto);
            }
        });

        return requests;
    }

    function createMessage(msgErrorBarElements: any[], containerId: string, message: string) {
        msgErrorBarElements.push(<div id={containerId}>
            <MessageBar
                messageBarType={MessageBarType.error}
                isMultiline={false}
                onDismiss={() => {
                    const messageDiv = document.getElementById(containerId);
                    if (messageDiv) {
                        messageDiv.parentNode!.removeChild(messageDiv);
                    }
                }}
            >
                {message}
            </MessageBar></div>
        );
    }

    function _onSubmitClicked() {
        const invalidCas: string[] = []
        Object.keys(formDatas).forEach((ca) => {
            const formData = formDatas[ca];
            if(formData.formState) {
                Object.keys(formData.formState).forEach((key) => {
                    formData.formState = {
                        ...formData.formState,
                        [key]: true
                    }
                });
            }
            if (!(formData.euSectoralBasis && formData.requestedInfo && formData.reasonForRequest)) {
                invalidCas.push(state.recipientAndRequestDetails.find(nca => nca.id === Number(ca))?.caCode ?? '');
            }
        });
        let msgErrorBarElem = document.getElementById('message-bar-placeholder');
        // Keep array of MessageBar elements to unmount them later
        let msgErrorBarElements: any = [];

        if (invalidCas.length > 0) {
            createMessage(msgErrorBarElements,  'message-bar-form-errors', strings.messageBarValidationError.replace("###", invalidCas.join(", ")));
        }
        if (Object.keys(formDatas).length === 0) {
            createMessage(msgErrorBarElements, 'message-bar-form-empty', strings.newRequestErrorNoNcaSelected);
        }

        if (msgErrorBarElements.length > 0) {
            ReactDOM.render(msgErrorBarElements, msgErrorBarElem);
        } else {
            if(msgErrorBarElem) {
                ReactDOM.unmountComponentAtNode(msgErrorBarElem);
            }
            toggleHideSubmitDialog();
        }
        setFormDatas({...formDatas});
    }
    async function _onSubmitOkClicked() {
        setFormSubmitted(true);
        const newRequestDto: NewRequestDto = {
            poiId: state.personOfInterest.PersonId,
            Action: 'Submit',
            Requests: convertFormDataToDto(),
        };
        try {
            if(!draftId) {
                let response = await postNewRequest(newRequestDto, auth.user?.id_token);
                if ((response as NewRequestEmailError).reqId) {
                    displayMessageBar(MessageBarType.error, (response as NewRequestEmailError).message, false);
                    setFormSubmitted(true);
                    addToUrlParam('reqId', (response as NewRequestEmailError).reqId);
                    toggleHideSubmitDialog();
                    return;
                }
                if(response){
                    addToUrlParam('reqId', response.toString());
                }
            } else {
                await putDraftRequest(newRequestDto, draftId, auth.user?.id_token);
            }
            displayMessageBar(MessageBarType.success, strings.newRequestSubmitSuccess, false);
        } catch (error: any) {
            let isEmailError = error.message.includes('email');
            let message = isEmailError ? error.message : 'Failed to submit request. '+error.message;
            displayMessageBar(MessageBarType.error, message, false);
            setFormSubmitted(!!isEmailError);
        }
        toggleHideSubmitDialog();
    }

    function setRecipients(selectedItems: RecipientAndRequestDto[], initialformDatas?: {[key: string] : FormData}) {
        const removedNcas = recipientsToShow.filter(existingRecipient =>
            !selectedItems.some(newRecipient => newRecipient.id === existingRecipient.id)
        );
        const localFormData = {...(initialformDatas ?? formDatas)};
        const updatedFormDatas = {...localFormData};
        if (removedNcas) {
            removedNcas.forEach((nca) => {
                if (updatedFormDatas[nca.id]) {
                    delete updatedFormDatas[nca.id];
                }
            })
        }
        const updatedRecipientsToShow = recipientsToShow.filter(existingRecipient =>
            selectedItems.some(newRecipient => newRecipient.id === existingRecipient.id)
        );

        selectedItems.forEach((nca) => {
            if (!updatedRecipientsToShow.some(recipient => recipient.id === nca.id)) {
                updatedRecipientsToShow.push(nca);
                const recipientData = state.recipientAndRequestDetails.find(rec => rec.id === nca.id);
                if (!localFormData[nca.id] && recipientData) {
                    updatedFormDatas[nca.id] = {...localFormData[nca.id]};
                    updatedFormDatas[nca.id].caInternalAssessmentNumber = recipientData.caInternalAssessmentNumber;
                    updatedFormDatas[nca.id].dateOfAssessment = recipientData.dateOfAssessment;
                }
            }
        });
        setFormDatas(updatedFormDatas);
        updatedRecipientsToShow.sort((a, b) => a.caCodeToDisplay.localeCompare(b.caCodeToDisplay));
        setRecipientsToShow(updatedRecipientsToShow);
    }

    function _onSubmitRecipientsDialogClicked() {
        toggleHideRecipientsDialog();
        setRecipients(newSelectedItems);
    }

    function addToUrlParam(name: string, value: string) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.append(name, value);
        setSearchParams(newSearchParams.toString());
    }

    async function _onDraftClicked() {
        const newRequestDto: NewRequestDto = {
            poiId: state.personOfInterest.PersonId,
            Action: 'Draft',
            Requests: convertFormDataToDto(),
        };
        try {
            if(!draftId) {
                let response = await postNewRequest(newRequestDto, auth.user?.id_token);
                setDraftId(response as string);
                if(response){
                    addToUrlParam('reqId', response.toString());
                }
            } else {
                await putDraftRequest(newRequestDto, draftId, auth.user?.id_token);
            }
            displayMessageBar(MessageBarType.success, strings.newRequestDraftSuccess, false);
        } catch (error: any) {
            let message = error.message.includes('email') ? error.message : 'Failed to submit request. '+error.message;
            displayMessageBar(MessageBarType.error, message, false);
            setFormSubmitted(false);
        }
        toggleHideDraftDialog();
    }

    const getPivotDropdownOptions = (): IDropdownOption[] => {
        if (state.euSectoralLegislatureBasis) {
            return state.euSectoralLegislatureBasis.map((item) => ({key: item.id, text: item.name}))
        }

        return [];
    };

    const getErrorMessageReasonForRequest = (value: string) => {
        return (formDatas[selectedTab]?.reasonForRequest?.length === 0 && initFormDatas[selectedTab]?.reasonForRequest !== value && formDatas[selectedTab]?.formState.reasonForRequest) ? 'Field is required' : '';
    }
    const getErrorMessageRequestedInfo = (value: string) => {
        return (formDatas[selectedTab]?.requestedInfo?.length === 0 && initFormDatas[selectedTab]?.requestedInfo !== value && formDatas[selectedTab]?.formState.requestedInfo) ? 'Field is required' : '';
    }
    const getErrorMessageEuSectoralBasis = (euSectoralBasis: number | null | undefined) => {
        return (!euSectoralBasis && formDatas[selectedTab]?.formState?.euSectoralBasis) ? 'Field is required' : undefined;
    }

    const renderGuidanceText = (html: string) => {
        // Content is admin-authored and trusted; DOMPurify default mode strips
        // scripts, event handlers and javascript: URIs while preserving all other HTML.
        const cleanHtml = DOMPurify.sanitize(html);
        return <div dangerouslySetInnerHTML={{ __html: cleanHtml }} />;
    };

    const onRenderSuffix = (props?: ITextFieldProps | undefined) => {
        return  <div>{maxCharLimit - (props?.value?.length ?? 0)}</div>
    };

    const pivots = recipientsToShow.map((value, index) => {
        return (
            <PivotItem headerText={value.caCodeToDisplay} itemKey={value.id.toString()} key={value.id}>
                <StackItem styles={stackStylesLabels}>
                    <Label className={styles.DisclaimerLabel}>
                        {strings.newRequestForInfoDisclaimer}
                    </Label>
                </StackItem>
                <Stack horizontal={true} horizontalAlign="space-between" verticalAlign="end"
                       tokens={themedLargeStackTokens}
                       className={styles.PivotLockedFieldAndSectoralDropdown}>
                    <StackItem className={styles.SearchStackItemSecondRow}>
                        <Label>{strings.requestForInfoIntAssessNo}</Label>
                        <TextField disabled={true} readOnly={true}
                                   value={formDatas[selectedTab]?.caInternalAssessmentNumber ?? ''}
                                   className={styles.DisabledTextField}/>
                    </StackItem>
                    <StackItem className={styles.SearchStackItemSecondRow}>
                        <Label>{strings.requestForInfoPOIAssessDate}</Label>
                        <TextField disabled={true} readOnly={true} value={formDatas[selectedTab]?.dateOfAssessment ?? ''}
                                   className={styles.DisabledTextField}/>
                    </StackItem>
                    <StackItem className={`${styles.SearchStackItemSecondRow} ${styles.SectoralDropdown}`}>
                        <Label id={'basis'+value.id.toString()} required={true}>{strings.newRequestForInfoRequestDetailsBasis}</Label>
                        <Dropdown className={styles.SearchStackItemThirdRow} options={getPivotDropdownOptions()}
                                  selectedKey={formDatas[selectedTab]?.euSectoralBasis ?? undefined}
                                  disabled={formSubmitted}
                                  aria-labelledby={'basis'+value.id.toString()}
                                  ariaLabel={strings.newRequestForInfoRequestDetailsBasis}
                                  errorMessage={getErrorMessageEuSectoralBasis(formDatas[selectedTab]?.euSectoralBasis)}
                                  onChange={(event, item) =>
                                      _handleDropDownChange(value, 'euSectoralBasis', item)
                                  }
                        />
                    </StackItem>
                </Stack>
                <Stack horizontal={true} horizontalAlign="space-between" verticalAlign="end"
                       tokens={themedLargeStackTokens}
                       className={styles.PivotRequestDetailsAndReason}>
                    <StackItem className={styles.SecondRowTextFields}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                            <Label id={'requestDetailsReason'+value.id.toString()} required={true}>{strings.newRequestForInfoRequestDetailsReason}</Label>
                            {fieldGuidance.reasonForRequest && (
                                <TooltipHost content={renderGuidanceText(fieldGuidance.reasonForRequest)} closeDelay={800}>
                                    <IconButton iconProps={{ iconName: 'Info' }} ariaLabel="Guidance for Reason for request" styles={{ root: { height: 20, width: 20, marginBottom: 4 }, icon: { fontSize: 14, color: '#605e5c' } }} />
                                </TooltipHost>
                            )}
                        </Stack>
                        <div style={{ position: 'relative' }}>
                            <TextField multiline rows={8}
                                       validateOnLoad={false}
                                       ariaLabel={strings.newRequestForInfoRequestDetailsReason}
                                       aria-labelledby={'requestDetailsReason'+value.id.toString()}
                                       disabled={formSubmitted}
                                       value={formDatas[selectedTab]?.reasonForRequest ?? ''}
                                       errorMessage={getErrorMessageReasonForRequest(formDatas[selectedTab]?.reasonForRequest)} validateOnFocusOut={true}
                                       onChange={(event, item) =>
                                           _handleInputChange(value.id, 'reasonForRequest', item)
                                       }
                                       className={styles.RecipientTextField}
                                       onRenderSuffix={onRenderSuffix}
                                       styles={{
                                           fieldGroup: [{
                                               borderColor: '#C2C2C2'
                                           }],
                                           suffix: {
                                               position: 'absolute',
                                               top: '-9px',
                                               right: '17px',
                                               backgroundColor: '#ECECEC',
                                               padding: '8px 4px',
                                               border: '1px solid #C2C2C2',
                                               borderRadius: '4px',
                                               textAlign: 'center',
                                               fontSize: '12px',
                                               zIndex: 10
                                           },
                                       }}
                            />
                        </div>
                    </StackItem>
                    <StackItem className={styles.SecondRowTextFields}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                            <Label id={'requestedInfo'+value.id.toString()} required={true}>{strings.newRequestForInfoRequestDetailsInfo}</Label>
                            {fieldGuidance.infoRequested && (
                                <TooltipHost content={renderGuidanceText(fieldGuidance.infoRequested)} closeDelay={800}>
                                    <IconButton iconProps={{ iconName: 'Info' }} ariaLabel="Guidance for Requested information" styles={{ root: { height: 20, width: 20, marginBottom: 4 }, icon: { fontSize: 14, color: '#605e5c' } }} />
                                </TooltipHost>
                            )}
                        </Stack>
                        <div style={{ position: 'relative' }}>
                            <TextField multiline rows={8} value={formDatas[selectedTab]?.requestedInfo ?? ''}
                                       validateOnLoad={false}
                                       disabled={formSubmitted}
                                       ariaLabel={strings.newRequestForInfoRequestDetailsInfo}
                                       aria-labelledby={'requestedInfo'+value.id.toString()}
                                       validateOnFocusOut={true} errorMessage={getErrorMessageRequestedInfo(formDatas[selectedTab]?.requestedInfo)}
                                       onChange={(event, item) =>
                                           _handleInputChange(value.id, 'requestedInfo', item)
                                       }
                                       className={styles.RecipientTextField}
                                       onRenderSuffix={onRenderSuffix}
                                       styles={{
                                           fieldGroup: [{
                                               borderColor: '#C2C2C2'
                                           }],
                                           suffix: {
                                               position: 'absolute',
                                               top: '-9px',
                                               right: '17px',
                                               backgroundColor: '#ECECEC',
                                               padding: '8px 4px',
                                               border: '1px solid #C2C2C2',
                                               borderRadius: '4px',
                                               textAlign: 'center',
                                               fontSize: '12px',
                                               zIndex: 10
                                           },
                                       }}
                            />
                        </div>
                    </StackItem>
                </Stack>
            </PivotItem>
        )
    })



    return (
        <Stack verticalAlign="space-evenly">
            <Stack horizontal={true} horizontalAlign="start" verticalAlign="end">
                <StackItem>
                    <Label className={styles.BigHeader}>
                        {strings.newRequestForInfoPivot}
                    </Label>
                </StackItem>
            </Stack>

            <InitiatorPoiFieldsComponent personOfInterest={state.personOfInterest}
                                         initiatorDetails={state.initiatorDetails}
                                         poiTitle={strings.newRequestForInfoPoiTitle}
                                            isView={false}
            />

            <Stack>
                <StackItem styles={stackStylesLabels}>
                    <Label className={styles.SmallHeader}>
                        {strings.newRequestForInfoRecipientsDetails}
                    </Label>
                </StackItem>
                <StackItem>
                    <Pivot overflowBehavior={'menu'} selectedKey={String(selectedTab)} onLinkClick={(item, e) =>
                        handleTabClick(item)
                    }>
                        {pivots}
                    </Pivot>
                </StackItem>
            </Stack>
            <Stack horizontal={true}
                   horizontalAlign="start"
                   tokens={buttonStackTokens}
                   className={styles.EditCopyButtonsStack}>
                <DefaultButton
                    text={strings.requestForInfoEditButton}
                    iconProps={{iconName: 'EditSolid12'}}
                    onClick={toggleHideRecipientsDialog}
                    className={styles.EditButton}
                    disabled={isUnauthorized || formSubmitted}
                />
                <DefaultButton
                    text={strings.requestForInfoCopyButton}
                    iconProps={{iconName: 'Copy'}}
                    onClick={toggleHideCopyDialog}
                    disabled={isUnauthorized || formSubmitted}
                    className={`${styles.CopyButton} ${isCopyHidden ? styles.hidden : ''}`}
                />

                <Dialog
                    hidden={hideCopyDialog}
                    onDismiss={toggleHideCopyDialog}
                    dialogContentProps={copyDialogContentProps}
                >
                    <DialogFooter>
                        <PrimaryButton onClick={_copyData} text="Copy" />
                        <DefaultButton onClick={toggleHideCopyDialog} text="Cancel" />
                    </DialogFooter>
                </Dialog>


            </Stack>
            <Stack
                horizontal={true}
                horizontalAlign="end"
                tokens={buttonStackTokens}
                className={styles.SubmitAndDraftButtons}>
                <PrimaryButton text={strings.requestForInfoSubmitButton} allowDisabledFocus onClick={_onSubmitClicked} disabled={isUnauthorized || formSubmitted} />
                <DefaultButton
                    text={strings.requestForInfoSaveDraftButton}
                    onClick={toggleHideDraftDialog}
                    disabled={isUnauthorized || formSubmitted}
                />
            </Stack>
            <Dialog
                hidden={hideSubmitDialog}
                onDismiss={toggleHideSubmitDialog}
                dialogContentProps={submitDialogContentProps}
            >
                <DialogFooter>
                    <PrimaryButton disabled={formSubmitted} onClick={_onSubmitOkClicked} ariaLabel={"Submit"} text="Submit" />
                    <DefaultButton disabled={formSubmitted} onClick={toggleHideSubmitDialog} text="Cancel" />
                </DialogFooter>
            </Dialog>
            <Dialog
                hidden={hideDraftDialog}
                onDismiss={toggleHideDraftDialog}
                dialogContentProps={draftDialogContentProps}
            >
                <DialogFooter>
                    <PrimaryButton onClick={_onDraftClicked} text="Save" />
                    <DefaultButton onClick={toggleHideDraftDialog} text="Cancel" />
                </DialogFooter>
            </Dialog>
            <Dialog
                hidden={hideRecipientsDialog}
                onDismiss={toggleHideRecipientsDialog}
                dialogContentProps={recipientsDialogContentProps}
                modalProps={modalProps}
                styles={editRecipientsDialogStyle}
            >
                <NcasDetailsListWithPagination
                    items={state.recipientAndRequestDetails}
                    selectedItems={recipientsToShow}
                    onSelectedItemsChange={handleSelectedItemsChange}
                />
                <DialogFooter>
                    <PrimaryButton onClick={_onSubmitRecipientsDialogClicked} text="Select and proceed" />
                    <DefaultButton onClick={toggleHideRecipientsDialog} text="Cancel" />
                </DialogFooter>
            </Dialog>
        </Stack>
    );
}

export default NewRequestForInformation;
